import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, User, Phone, Mail, Lock, Home, MapPin,
  Flag, Settings, Calendar, Briefcase, Users, UserCheck, Loader,
  Eye, EyeOff, Shield, Server, CheckCircle2, XCircle, Clock,
  Building, Hash, Globe, Key, FileText
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { authHelpers, employeesApi } from '../services/api';

function MyProfile() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.textColor === '#FFFFFF' || currentTheme?.textColor?.toLowerCase() === '#ffffff' || currentTheme?.color === '#0f172a';
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const currentUser = authHelpers.getCurrentUser();
        const userId = authHelpers.getUserId() || currentUser?.id || currentUser?.employeeId;

        if (!userId) {
          console.warn('No user ID found in session.');
          setLoading(false);
          return;
        }

        let empData = null;
        try {
          empData = await employeesApi.getEmployeeById(userId);
        } catch (err) {
          console.warn('Direct getEmployeeById failed, attempting email fallback:', err);
        }

        // Fallback matching by email if direct ID lookup fails or is empty
        if (!empData || !empData.id) {
          const userEmail = authHelpers.getUserEmail() || currentUser?.email;
          const allEmployees = await employeesApi.getAllEmployees();
          empData = allEmployees?.find(
            (e) => (e.id || e.employeeId) === userId || (userEmail && (e.email || e.emailId) === userEmail)
          );
        }

        // Check for plain-text password fallback if needed
        const sessionPass = sessionStorage.getItem('plainPassword');
        if (empData && !empData.password && sessionPass) {
          empData.password = sessionPass;
        }

        setProfileData(empData);
      } catch (error) {
        console.error('Error fetching employee profile:', error);
      } finally {
        setLoading(false);
      }
    };

    if (authHelpers.isAuthenticated()) {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className={`flex items-center justify-center min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#0f172a]' : 'bg-[#F8F9FA]'}`}>
        <Loader className="w-10 h-10 animate-spin" style={{ color: currentTheme?.color || '#449978' }} />
      </div>
    );
  }

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return '-/-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-/-';
    try {
      return new Date(dateStr).toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Parse IP addresses
  const ipList = Array.isArray(profileData?.ipAddresses)
    ? profileData.ipAddresses
    : (profileData?.ipAddress ? profileData.ipAddress.split(',').map((ip) => ip.trim()).filter(Boolean) : []);

  return (
    <div className={`p-4 md:p-6 min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#0f172a]' : 'bg-[#F8F9FA]'}`}>
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate(-1)}
            className={`p-2 rounded-full border transition-all ${
              isDark ? 'border-white/20 text-white hover:bg-white/10' : 'hover:bg-black/5'
            }`}
            style={!isDark ? { borderColor: `${currentTheme?.color || '#449978'}40`, color: currentTheme?.color || '#449978' } : {}}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1
              className={`text-2xl font-bold ${isDark ? 'text-white' : ''}`}
              style={!isDark ? { color: currentTheme?.color || '#449978' } : {}}
            >
              My Profile
            </h1>
            <p className="text-xs text-gray-400">View and manage your personal, employment and technical details</p>
          </div>
        </div>

        {/* Status Badge */}
        <div className="flex items-center space-x-2">
          <div
            className={`px-3 py-1.5 rounded-full border text-xs font-semibold flex items-center space-x-1.5 ${
              profileData?.active !== false
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500'
                : 'bg-red-500/10 border-red-500/30 text-red-500'
            }`}
          >
            {profileData?.active !== false ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>Account Active</span>
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4 text-red-500" />
                <span>Account Inactive</span>
              </>
            )}
          </div>
          {profileData?.role && (
            <div
              className={`px-3 py-1.5 rounded-full border text-xs font-bold uppercase tracking-wider ${
                isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-100 border-gray-200 text-gray-700'
              }`}
            >
              {profileData.role}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column (Main Details) */}
        <div className="lg:col-span-8 space-y-6">
          {/* Card 1: Basic Information */}
          <div className={`rounded-2xl p-6 border shadow-sm ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-100'}`}>
            <h2 className={`text-lg font-bold mb-6 ${isDark ? 'text-white' : ''}`} style={!isDark ? { color: currentTheme?.color } : {}}>
              Basic Details
            </h2>

            <div className="flex flex-col sm:flex-row items-start gap-6">
              {/* Profile Avatar */}
              <div
                className={`w-20 h-20 rounded-2xl flex items-center justify-center text-white text-3xl font-extrabold shadow-md flex-shrink-0 border-2 ${
                  isDark ? 'border-white/10' : 'border-white'
                }`}
                style={{ backgroundColor: currentTheme?.color || '#449978' }}
              >
                {profileData?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>

              <div className="flex-1 w-full space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Name */}
                  <div className="flex items-start space-x-3">
                    <User className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="overflow-hidden">
                      <p className="text-xs text-gray-400 font-medium mb-0.5">Full Name</p>
                      <p className={`text-sm font-semibold truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                        {profileData?.name || '-/-'}
                      </p>
                    </div>
                  </div>

                  {/* Contact Number */}
                  <div className="flex items-start space-x-3">
                    <Phone className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <div className="overflow-hidden">
                      <p className="text-xs text-gray-400 font-medium mb-0.5">Contact Number</p>
                      <p className={`text-sm font-semibold truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                        {profileData?.contactNumber || profileData?.personalNumber || '-/-'}
                      </p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start space-x-3">
                    <Mail className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div className="overflow-hidden">
                      <p className="text-xs text-gray-400 font-medium mb-0.5">Email Address</p>
                      <p className={`text-sm font-semibold truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                        {profileData?.email || '-/-'}
                      </p>
                    </div>
                  </div>

                  {/* Gender */}
                  <div className="flex items-start space-x-3">
                    <Users className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <div className="overflow-hidden">
                      <p className="text-xs text-gray-400 font-medium mb-0.5">Gender</p>
                      <p className={`text-sm font-semibold capitalize ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                        {profileData?.gender ? profileData.gender.toLowerCase() : '-/-'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Password & Employee ID Quick Badges */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-gray-100 dark:border-white/5">
                  {/* Password Toggle */}
                  <div
                    className={`px-3 py-2 rounded-xl border flex items-center justify-between ${
                      isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center space-x-2.5 overflow-hidden flex-1">
                      <div
                        className={`p-1.5 rounded-lg border flex-shrink-0 ${
                          isDark ? 'bg-white/10 border-white/5' : 'bg-white border-gray-200'
                        }`}
                      >
                        <Lock className="w-3.5 h-3.5" style={{ color: currentTheme?.color || '#449978' }} />
                      </div>
                      <div className="overflow-hidden flex-1">
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Password</p>
                        <p
                          className={`text-xs font-semibold tracking-wider truncate ${isDark ? 'text-white' : ''}`}
                          style={!isDark ? { color: currentTheme?.color } : {}}
                        >
                          {showPassword ? (profileData?.password || '-/-') : '••••••••'}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`p-1.5 rounded-lg transition-all flex-shrink-0 ${
                        isDark ? 'text-gray-400 hover:bg-white/10 hover:text-white' : 'hover:bg-white text-gray-500'
                      }`}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Employee Code / Agent ID */}
                  <div
                    className={`px-3 py-2 rounded-xl border flex items-center space-x-2.5 ${
                      isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div
                      className={`p-1.5 rounded-lg border flex-shrink-0 ${
                        isDark ? 'bg-white/10 border-white/5' : 'bg-white border-gray-200'
                      }`}
                    >
                      <Hash className="w-3.5 h-3.5" style={{ color: currentTheme?.color || '#449978' }} />
                    </div>
                    <div className="overflow-hidden">
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Employee Code / ID</p>
                      <p className={`text-xs font-semibold truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>
                        {profileData?.employeeCode || profileData?.employeeId || '-/-'}
                        {profileData?.employeeId && profileData?.employeeCode ? ` (${profileData.employeeId})` : ''}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: Address Information */}
          <div className={`rounded-2xl p-6 border shadow-sm ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-100'}`}>
            <h2 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : ''}`} style={!isDark ? { color: currentTheme?.color } : {}}>
              Address Information
            </h2>
            <div className="space-y-4">
              {/* Street Address */}
              <div
                className={`p-3.5 rounded-xl border flex items-start space-x-3 ${
                  isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div
                  className={`p-2 rounded-lg border flex-shrink-0 mt-0.5 ${
                    isDark ? 'bg-white/10 border-white/5' : 'bg-white border-gray-200'
                  }`}
                >
                  <Home className="w-4 h-4 text-purple-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium mb-0.5">Street Address</p>
                  <p className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    {profileData?.addressStreet || profileData?.address || '-/-'}
                  </p>
                </div>
              </div>

              {/* Landmark, City, District, State, Pincode */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pt-1">
                <div className="flex items-start space-x-3">
                  <MapPin className="w-4 h-4 text-orange-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400 font-medium mb-0.5">Landmark</p>
                    <p className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      {profileData?.landmark || '-/-'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Building className="w-4 h-4 text-cyan-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400 font-medium mb-0.5">City</p>
                    <p className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      {profileData?.city || '-/-'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Flag className="w-4 h-4 text-emerald-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400 font-medium mb-0.5">District</p>
                    <p className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      {profileData?.district || '-/-'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Globe className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400 font-medium mb-0.5">State</p>
                    <p className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      {profileData?.state || '-/-'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <MapPin className="w-4 h-4 text-rose-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400 font-medium mb-0.5">Pincode</p>
                    <p className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      {profileData?.pincode || '-/-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card 3: Technical & Campaign Settings */}
          <div className={`rounded-2xl p-6 border shadow-sm ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-100'}`}>
            <h2 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : ''}`} style={!isDark ? { color: currentTheme?.color } : {}}>
              Technical & Campaign Details
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-5">
              <div
                className={`p-3.5 rounded-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}
              >
                <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">Agent ID</p>
                <p className={`text-sm font-bold truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {profileData?.agentId || '-/-'}
                </p>
              </div>

              <div
                className={`p-3.5 rounded-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}
              >
                <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">Dialer Code</p>
                <p className={`text-sm font-bold truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {profileData?.dialerCode || '-/-'}
                </p>
              </div>

              <div
                className={`p-3.5 rounded-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}
              >
                <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">Order Campaign</p>
                <p className={`text-sm font-bold truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {profileData?.orderCampaign || '-/-'}
                </p>
              </div>

              <div
                className={`p-3.5 rounded-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}
              >
                <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">Dialer Campaign</p>
                <p className={`text-sm font-bold truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {profileData?.dailerCampaign || '-/-'}
                </p>
              </div>
            </div>

            {/* System Permissions Flags */}
            <div className="mb-5">
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-2.5">System Permissions</p>
              <div className="flex flex-wrap gap-2.5">
                <div
                  className={`px-3 py-1.5 rounded-lg border text-xs font-semibold flex items-center space-x-1.5 ${
                    profileData?.maskNumber
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500'
                      : (isDark ? 'bg-white/5 border-white/10 text-gray-400' : 'bg-gray-100 border-gray-200 text-gray-500')
                  }`}
                >
                  {profileData?.maskNumber ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                  <span>Mask Number: {profileData?.maskNumber ? 'Enabled' : 'Disabled'}</span>
                </div>

                <div
                  className={`px-3 py-1.5 rounded-lg border text-xs font-semibold flex items-center space-x-1.5 ${
                    profileData?.downloadMaskedNumber
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500'
                      : (isDark ? 'bg-white/5 border-white/10 text-gray-400' : 'bg-gray-100 border-gray-200 text-gray-500')
                  }`}
                >
                  {profileData?.downloadMaskedNumber ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                  <span>Download Masked: {profileData?.downloadMaskedNumber ? 'Enabled' : 'Disabled'}</span>
                </div>

                <div
                  className={`px-3 py-1.5 rounded-lg border text-xs font-semibold flex items-center space-x-1.5 ${
                    profileData?.logPermission
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500'
                      : (isDark ? 'bg-white/5 border-white/10 text-gray-400' : 'bg-gray-100 border-gray-200 text-gray-500')
                  }`}
                >
                  {profileData?.logPermission ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                  <span>Log Permission: {profileData?.logPermission ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
            </div>

            {/* Allowed IP Addresses */}
            {ipList.length > 0 && (
              <div>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-2 flex items-center">
                  <Server className="w-3.5 h-3.5 mr-1.5 text-blue-400" />
                  Allowed IP Addresses
                </p>
                <div className="flex flex-wrap gap-2">
                  {ipList.map((ip, index) => (
                    <span
                      key={index}
                      className={`px-2.5 py-1 rounded-md text-xs font-mono font-medium border ${
                        isDark ? 'bg-white/5 border-white/10 text-gray-300' : 'bg-gray-100 border-gray-200 text-gray-700'
                      }`}
                    >
                      {ip}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column (Employment & System Log Info) */}
        <div className="lg:col-span-4 space-y-6">
          {/* Card 4: Employment Details */}
          <div className={`rounded-2xl p-6 border shadow-sm ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-100'}`}>
            <h2 className={`text-lg font-bold mb-5 ${isDark ? 'text-white' : ''}`} style={!isDark ? { color: currentTheme?.color } : {}}>
              Employment Details
            </h2>

            <div className="space-y-4">
              <div>
                <p className="text-xs text-gray-400 font-medium mb-1 uppercase tracking-wide flex items-center">
                  <Calendar className="w-3.5 h-3.5 mr-1.5" style={{ color: currentTheme?.color || '#449978' }} />
                  Date of Joining
                </p>
                <p className={`text-sm font-bold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                  {formatDate(profileData?.dateOfJoining)}
                </p>
              </div>

              <div className="border-t border-gray-100 dark:border-white/5 pt-3">
                <p className="text-xs text-gray-400 font-medium mb-1 uppercase tracking-wide flex items-center">
                  <Building className="w-3.5 h-3.5 mr-1.5" style={{ color: currentTheme?.color || '#449978' }} />
                  Department
                </p>
                <p className={`text-sm font-bold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                  {profileData?.departmentName || '-/-'}
                  {profileData?.departmentId ? ` (ID: ${profileData.departmentId})` : ''}
                </p>
              </div>

              <div className="border-t border-gray-100 dark:border-white/5 pt-3">
                <p className="text-xs text-gray-400 font-medium mb-1 uppercase tracking-wide flex items-center">
                  <Briefcase className="w-3.5 h-3.5 mr-1.5" style={{ color: currentTheme?.color || '#449978' }} />
                  Designation
                </p>
                <p className={`text-sm font-bold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                  {profileData?.designationName || profileData?.designation || '-/-'}
                </p>
              </div>

              <div className="border-t border-gray-100 dark:border-white/5 pt-3">
                <p className="text-xs text-gray-400 font-medium mb-1 uppercase tracking-wide flex items-center">
                  <Users className="w-3.5 h-3.5 mr-1.5" style={{ color: currentTheme?.color || '#449978' }} />
                  Team
                </p>
                <p className={`text-sm font-bold ${isDark ? 'text-white' : ''}`} style={!isDark ? { color: currentTheme?.color } : {}}>
                  {profileData?.teamName || '-/-'}
                  {profileData?.teamId ? ` (ID: ${profileData.teamId})` : ''}
                </p>
              </div>

              <div className="border-t border-gray-100 dark:border-white/5 pt-3">
                <p className="text-xs text-gray-400 font-medium mb-1 uppercase tracking-wide flex items-center">
                  <UserCheck className="w-3.5 h-3.5 mr-1.5" style={{ color: currentTheme?.color || '#449978' }} />
                  Manager / Team Leader
                </p>
                <p className={`text-sm font-bold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                  {profileData?.managerName || profileData?.teamName || '-/-'}
                </p>
              </div>
            </div>
          </div>

          {/* Card 5: System Record Metadata */}
          <div className={`rounded-2xl p-6 border shadow-sm ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-100'}`}>
            <h2 className={`text-sm font-bold mb-4 uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              System Record Info
            </h2>

            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-400 flex items-center">
                  <Clock className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                  Created At:
                </span>
                <span className={`font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {formatDateTime(profileData?.createdAt)}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-400 flex items-center">
                  <Clock className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                  Last Updated:
                </span>
                <span className={`font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {formatDateTime(profileData?.updatedAt)}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs pt-2 border-t border-gray-100 dark:border-white/5">
                <span className="text-gray-400 flex items-center">
                  <Shield className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                  Database Record ID:
                </span>
                <span className="font-mono font-bold text-gray-500">
                  #{profileData?.id || '-/-'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyProfile;
